import {
  FormControl,
  FormLabel,
  Switch,
  Checkbox,
  Text as ChakraText,
  Flex,
  Link,
  VStack,
  HStack,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Field, FieldProps } from 'formik'
import { EndpointFormProps } from '../../interfaces'
import { SettingsToggleBox } from '../Settings'
import { Select } from '@chakra-ui/select'
import { UrlRewriterEum } from '../../constants'

export interface OptionType {
  value: string
  label: string
}

const options: OptionType[] = [
  { value: UrlRewriterEum.CLOUDINARY, label: 'Cloudinary' },
  { value: UrlRewriterEum.IMGIX, label: 'Imgix' },
  { value: UrlRewriterEum.AKAMAI, label: 'Akamai' },
]

const UrlRewriterForm = ({ values, handleChange }): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <SettingsToggleBox>
      <Flex justifyContent="space-between" width="100%">
        <ChakraText fontWeight="700">
          {t('enabled_url_rewriter', 'Enable URL rewriter')}
        </ChakraText>
        <Field name="enableURLRewriter">
          {({ field }: FieldProps<boolean | undefined, EndpointFormProps>) => (
            <Switch
              name="enableURLRewriter"
              id="enableURLRewriter"
              _focus={{ boxShadow: 'none', outline: 'none' }}
              onFocus={e => e.preventDefault()}
              isChecked={field.value ? true : false}
              onChange={e => {
                handleChange({
                  target: {
                    name: 'enableURLRewriter',
                    value: e.target.checked,
                  },
                })
                if (e.target.checked) {
                  handleChange({
                    target: {
                      name: 'urlRewriter',
                      value: UrlRewriterEum.CLOUDINARY,
                    },
                  })
                  handleChange({
                    target: {
                      name: 'cloudinaryKeepAssetDeliveryTypes',
                      value: false,
                    },
                  })
                  handleChange({
                    target: {
                      name: 'cloudinaryStrictMode',
                      value: false,
                    },
                  })
                }
              }}
            />
          )}
        </Field>
      </Flex>
      <ChakraText>
        {t(
          'enabled_url_rewriter_description',
          "After migrating to ImageKit.io from another service, ImageKit.io can translate your pre-existing transformation URLs written in the syntax of your previous service provider to ImageKit's syntax. ",
        )}
        <Link isExternal href="https://docs.imagekit.io/features/url-rewriters">
          {t('read_more', 'Read More')}
        </Link>
      </ChakraText>

      {values?.enableURLRewriter && (
        <VStack alignItems={'flex-start'} w="100%" spacing={0}>
          <FormLabel>
            {t('select_url_rewriter', 'Select URL rewriter')}
          </FormLabel>
          <HStack gap="2em" alignItems={'center'} w="100%">
            <Field name="urlRewriter">
              {({ field }: FieldProps<string, EndpointFormProps>) => (
                <FormControl maxWidth="250px">
                  <Select
                    maxWidth="250px"
                    value={field.value}
                    onChange={event => {
                      if (event.target.value === UrlRewriterEum.IMGIX) {
                        handleChange({
                          target: {
                            name: 'cloudinaryKeepAssetDeliveryTypes',
                            value: false,
                          },
                        })
                        handleChange({
                          target: {
                            name: 'cloudinaryStrictMode',
                            value: false,
                          },
                        })
                      }
                      handleChange({
                        target: {
                          name: 'urlRewriter',
                          value: event.target.value,
                        },
                      })
                    }}
                    disabled={!values?.enableURLRewriter}
                  >
                    {options.map(x => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Field>
          </HStack>
          {values?.urlRewriter === UrlRewriterEum.CLOUDINARY && (
            <VStack
              gap="2"
              alignItems="center"
              justifyContent="flex-start"
              paddingTop="1em"
            >
              <Field name="cloudinaryKeepAssetDeliveryTypes">
                {({
                  field,
                }: FieldProps<boolean | undefined, EndpointFormProps>) => (
                  <FormControl>
                    <Checkbox
                      isChecked={field.value ? true : false}
                      onChange={event => {
                        handleChange({
                          target: {
                            name: 'cloudinaryKeepAssetDeliveryTypes',
                            value: event.target.checked,
                          },
                        })
                      }}
                    >
                      {t(
                        'cloudinaryKeepAssetDeliveryTypes',
                        'Keep cloudinary asset and delivery types',
                      )}
                    </Checkbox>
                  </FormControl>
                )}
              </Field>

              <Field name="cloudinaryStrictMode">
                {({
                  field,
                }: FieldProps<boolean | undefined, EndpointFormProps>) => (
                  <FormControl>
                    <Checkbox
                      isChecked={field.value ? true : false}
                      onChange={event => {
                        handleChange({
                          target: {
                            name: 'cloudinaryStrictMode',
                            value: event.target.checked,
                          },
                        })
                      }}
                    >
                      {t(
                        'cloudinary_skip_rewrite_description',
                        'Skip rewrite if unsupported transformation is present',
                      )}
                    </Checkbox>
                  </FormControl>
                )}
              </Field>
            </VStack>
          )}
        </VStack>
      )}
    </SettingsToggleBox>
  )
}

export default UrlRewriterForm
